import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { FolderOpen, ExternalLink, Loader2 } from 'lucide-react';
import { clientPortalAPI, serviceDeliveryAPI } from '../lib/api';
import { DRIVE_TIPOS } from './DriveFoldersSection';

export const useClientDriveFolders = (teamClientId, isTeamViewer, withRoot = false) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    const p = isTeamViewer
      ? (teamClientId ? serviceDeliveryAPI.getDriveFolders(teamClientId).then((r) => ({ folders: (r.folders || []).filter((f) => f.url), root: r.client_drive_root || null })) : Promise.resolve({ folders: [], root: null }))
      : clientPortalAPI.getDriveFolders().then((r) => ({ folders: r.folders || [], root: r.root || null }));
    p.then(setData).catch(() => setData({ folders: [], root: null }));
  }, [teamClientId, isTeamViewer]);
  if (!data) return null;
  return withRoot ? data : data.folders;
};

function FolderLink({ f }) {
  return (
    <a href={f.url} target="_blank" rel="noreferrer"
      className="group flex items-start gap-3 p-3 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors"
      data-testid={`portal-drive-folder-${f.id}`}>
      <FolderOpen className="w-5 h-5 text-primary shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{f.nombre}</p>
        {f.nota && <p className="text-xs text-muted-foreground mt-0.5">{f.nota}</p>}
      </div>
      <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
    </a>
  );
}

export default function ClientDriveFolders({ teamClientId = null, isTeamViewer = false }) {
  const data = useClientDriveFolders(teamClientId, isTeamViewer, true);
  const folders = data?.folders || [];
  const root = data?.root || null;
  if (data === null) return <div className="py-6 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (isTeamViewer && !teamClientId) return null;
  return (
    <Card data-testid="portal-drive-folders">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg"><FolderOpen className="w-5 h-5 text-primary" /> Tus carpetas de Drive</CardTitle>
        <p className="text-xs text-muted-foreground">Accesos directos a las carpetas de tu Drive que has compartido con Karma Digital, para que subas el material directamente. Se abren en una pestaña nueva.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        {root && (
          <a href={root.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-lg border-2 border-sky-200 bg-sky-50 hover:border-sky-400 transition-colors" data-testid="portal-drive-root">
            <FolderOpen className="w-6 h-6 text-sky-700 shrink-0" />
            <div className="min-w-0 flex-1"><p className="text-sm font-semibold truncate">Tu carpeta principal{root.name ? ` · ${root.name}` : ''}</p><p className="text-xs text-muted-foreground">La carpeta de tu Drive que compartes con Karma Digital</p></div>
            <ExternalLink className="w-4 h-4 text-sky-700" />
          </a>
        )}
        {folders.length === 0 && !root ? (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="portal-drive-empty">Aún no hay carpetas compartidas.</p>
        ) : Object.entries(DRIVE_TIPOS).map(([k, t]) => {
          const list = folders.filter((f) => (f.tipo || 'subir') === k);
          if (!list.length) return null;
          return (
            <div key={k} data-testid={`portal-drive-group-${k}`}>
              <p className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border mb-2 ${t.cls}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {list.map((f) => <FolderLink key={f.id} f={f} />)}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
